/**
 * Manages element resize operations via drag handles.
 * Tracks resize state, applies width changes, and shows the grid during resize.
 */
export default class ResizeManager {
  /**
   * @param {HTMLElement} canvasElement - The canvas container element
   */
  constructor(canvasElement) {
    this.canvasElement = canvasElement;
    this.isResizing = false;
    this.hasResized = false;
    this.startX = 0;
    this.startY = 0;
    this.startWidth = 0;
    this.startHeight = 0;
    this.currentElement = null;
  }

  /**
   * Initiates a resize operation. Shows the canvas grid while resizing.
   * For image elements, resizes the inner img tag rather than the container.
   *
   * @param {MouseEvent} event - The mousedown event from the resize handle
   * @param {HTMLElement} element - The element being resized
   */
  startResize(event, element) {
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.hasResized = false;
    this.currentElement = element;
    this.startX = event.clientX;
    this.startY = event.clientY;

    if (element.dataset.type === 'image') {
      const img = element.querySelector('img');
      this.startWidth = img.offsetWidth;
      this.startHeight = img.offsetHeight;
    } else {
      this.startWidth = element.offsetWidth;
      this.startHeight = element.offsetHeight;
    }

    this.canvasElement.classList.add('show-grid');
  }

  /**
   * Handles mouse movement during resize. Enforces a minimum width of 50px.
   * @param {MouseEvent} event - The mousemove event
   */
  handleResizeMove(event) {
    if (!this.isResizing || !this.currentElement) {
      return;
    }

    const deltaX = event.clientX - this.startX;

    if (deltaX !== 0) {
      this.hasResized = true;
    }

    if (this.currentElement.dataset.type === 'image') {
      const img = this.currentElement.querySelector('img');
      const newWidth = Math.max(50, this.startWidth + deltaX);
      img.style.width = newWidth + 'px';
    } else {
      const newWidth = Math.max(50, this.startWidth + deltaX);
      this.currentElement.style.width = newWidth + 'px';
    }
  }

  /**
   * Ends the resize operation and hides the canvas grid.
   * @returns {boolean|null} Whether the element was actually resized, or null if no resize was active
   */
  endResize() {
    if (!this.isResizing) return null;

    const hasResized = this.hasResized;

    this.isResizing = false;
    this.hasResized = false;
    this.currentElement = null;
    this.canvasElement.classList.remove('show-grid');

    return hasResized;
  }

  isResizingActive() {
    return this.isResizing;
  }
}
